/**
 * Step2ScopeSelect Component
 * Step 2: Project Scope 선택 — SVG Preview + Scope cards
 */

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import DiagramPane from './DiagramPane';
import { WIZARD_DIAGRAM_ROW_SX, WIZARD_SIDE_COLUMN_SX } from './wizardLayout';
import type { WizardFormData } from './types';
import ScopeSelector from '@/features/scope/ScopeSelector';
import { groupScopes, restrictScopes, roleCountsForCoverage, scopeShortLabel, svgComponentsForScopes, toggleSystemScope, type ScopeId } from '@/types/scope';

interface Step2ScopeSelectProps {
  data: WizardFormData;
  onChange: (data: Partial<WizardFormData>) => void;
}

const Step2ScopeSelect: React.FC<Step2ScopeSelectProps> = ({ data, onChange }) => {
  // 선택한 scope(그룹 전체/계통 단위)가 하이라이트
  const highlightedComponents = useMemo(() => svgComponentsForScopes(data.scope), [data.scope]);

  // ScopeSelector 는 항상 정규화된 값을 낸다.
  const handleScopeChange = (next: ScopeId[]) => {
    // 프로젝트 scope에서 빠진 항목은 계통 모델 파티션의 scope에서도 빠진다 (그룹이 계통 일부로 줄면
    // 그 계통으로 축소; 프로젝트 scope 를 다 비우면 파티션도 비운다). 제어기·시나리오의 "작용 계통"은
    // 프로젝트 scope 밖이어도 되니 손대지 않는다.
    const updatedPartitions = data.partitions.map((p) =>
      roleCountsForCoverage(p.role) ? { ...p, scope: next.length ? restrictScopes(p.scope, next) : [] } : p,
    );
    onChange({ scope: next, partitions: updatedPartitions });
  };

  return (
    <Box sx={WIZARD_DIAGRAM_ROW_SX}>
      {/* 좌측: 계통도 — 3단계와 같은 상자 규칙(DiagramPane) */}
      <DiagramPane
        highlightedComponents={highlightedComponents}
        onComponentClick={(id) => handleScopeChange(toggleSystemScope(data.scope, id))}
      >
        <Typography variant="caption" color="text.secondary">
          선택한 계통이 하이라이트됩니다 — 그림의 요소를 클릭해도 토글됩니다
        </Typography>
      </DiagramPane>

      {/* 우측: Scope 선택 카드 */}
      <Box sx={{ ...WIZARD_SIDE_COLUMN_SX, py: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Project Scope
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          프로젝트에서 다룰 시스템 범위를 선택하세요. 그룹 전체를 켜거나, 펼쳐서 계통 단위로 세분할 수 있습니다.<br />
          선택한 Scope 내에서 Partition을 구성할 수 있습니다.
        </Typography>

        {/* Scope 선택기 — 그룹 카드 + 세분(계통) 체크리스트 */}
        <ScopeSelector value={data.scope} onChange={handleScopeChange} />

        {/* 선택 요약 */}
        <Box sx={{ mt: 'auto', pt: 2.5 }}>
          {data.scope.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              선택된 Scope:{' '}
              {groupScopes(data.scope)
                .map(({ ids }) => ids.map(scopeShortLabel).join(', '))
                .join(' · ')}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Step2ScopeSelect;
